import { useEffect, useState } from "react";
import { Button } from "./ui/Button";
import { Panel } from "./Surface";
import { Stars } from "./Stars";
import { inputClass } from "./FormKit";
import { listReviews, canReview, submitReview, removeReview, type Review, type ReviewAggregate } from "../lib/reviews";

function RatingPicker({ value, onChange }: { value: number; onChange: (n: number) => void }) {
  return (
    <div className="flex gap-1" role="radiogroup" aria-label="Your rating">
      {[1, 2, 3, 4, 5].map((n) => (
        <button key={n} type="button" aria-label={`${n} star${n > 1 ? "s" : ""}`} aria-checked={n === value} role="radio"
          onClick={() => onChange(n)} className={`text-2xl transition-colors ${n <= value ? "text-accent" : "text-line hover:text-muted"}`}>★</button>
      ))}
    </div>
  );
}

export function ReviewsSection({ profileId, name, isSelf }: { profileId: string; name: string; isSelf: boolean }) {
  const [reviews, setReviews] = useState<Review[]>([]);
  const [agg, setAgg] = useState<ReviewAggregate>({ avg: 0, count: 0 });
  const [me, setMe] = useState("");
  const [gate, setGate] = useState<{ can: boolean; reason: string } | null>(null);
  const [rating, setRating] = useState(0);
  const [body, setBody] = useState("");
  const [busy, setBusy] = useState(false);
  const [open, setOpen] = useState(false);

  async function load() {
    const r = await listReviews(profileId);
    setReviews(r.reviews);
    setAgg(r.aggregate);
    setMe(r.me);
    const mine = r.reviews.find((x) => x.author_account_id === r.me);
    if (mine) { setRating(mine.rating); setBody(mine.body ?? ""); }
  }
  useEffect(() => {
    void load();
    if (!isSelf) canReview(profileId).then(setGate).catch(() => undefined);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [profileId]);

  const myReview = reviews.find((x) => x.author_account_id === me);

  async function submit() {
    if (rating < 1) return;
    setBusy(true);
    try { await submitReview(profileId, rating, body); setOpen(false); await load(); }
    finally { setBusy(false); }
  }
  async function remove() {
    setBusy(true);
    try { await removeReview(profileId); setRating(0); setBody(""); await load(); }
    finally { setBusy(false); }
  }

  return (
    <Panel title="Reviews" action={agg.count > 0 ? <Stars value={agg.avg} count={agg.count} /> : undefined}>
      {!isSelf && gate?.can && (
        <div className="rounded-lg border border-line bg-surface-2 p-4">
          {open || myReview ? (
            <div className="grid gap-3">
              <span className="text-sm font-bold">{myReview ? "Your review" : `Review ${name}`}</span>
              <RatingPicker value={rating} onChange={setRating} />
              <textarea className={inputClass} rows={3} maxLength={2000} placeholder="Share your experience (optional)" value={body} onChange={(e) => setBody(e.target.value)} aria-label="Review" />
              <div className="flex items-center gap-2">
                <Button onClick={submit} disabled={busy || rating < 1}>{myReview ? "Update review" : "Post review"}</Button>
                {myReview && <button type="button" onClick={remove} disabled={busy} className="font-mono text-xs font-bold text-danger">Delete</button>}
                {!myReview && open && <button type="button" onClick={() => setOpen(false)} className="font-mono text-xs text-muted">Cancel</button>}
              </div>
            </div>
          ) : (
            <button type="button" onClick={() => { setOpen(true); setRating(5); }} className="text-sm font-bold text-accent-strong">Write a review →</button>
          )}
        </div>
      )}
      {!isSelf && gate && !gate.can && (
        <p className="font-mono text-xs text-muted">{gate.reason}.</p>
      )}

      {reviews.length === 0 ? (
        <p className="font-mono text-sm text-muted">No reviews yet.</p>
      ) : (
        <ul className="grid gap-3">
          {reviews.map((r) => (
            <li key={r.id} className="grid gap-1 border-t border-line py-3 first:border-t-0">
              <div className="flex items-center justify-between gap-2">
                <span className="font-bold">{r.author_name ?? "RecruitTrucker member"}{r.author_account_id === me ? " (you)" : ""}</span>
                <Stars value={r.rating} />
              </div>
              {r.body && <p className="whitespace-pre-wrap text-sm text-muted">{r.body}</p>}
              <span className="font-mono text-[10px] text-muted">{new Date(r.created_at).toLocaleDateString()}</span>
            </li>
          ))}
        </ul>
      )}
    </Panel>
  );
}
